import React, { useEffect, useState } from "react";

import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Popover from "@mui/material/Popover";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AirplaneIcon from "../../public/static/2d-models/airport/AirplaneIcon.svg";

import MVT from "ol/format/MVT.js";
import VectorTileLayer from "ol/layer/VectorTile.js";
import VectorTileSource from "ol/source/VectorTile.js";

import { getBorderFromMapLayerOption, getTileserverStatuses } from "./mapUtil";
import { GetLaancAirspaceStyle, GetLaancStyle, getSpecialUseAirspaceStyle, GetLayer } from "../map/mapUtil";

import { useEnv } from "../contexts/envContext";

import useMapDropdownStyles from "../hooks/useMapDropdownStyles";
import useExternalFlightsLayer from "../hooks/useExternalFlightsLayer";

const MapLaancLayersDropdown = ({
    map,
    mapLayersOpen,
    setMapLayersOpen,
    mapLayersAnchorEl,
    setMapLayersAnchorEl,
    layers,
    airspaceSubLayers,
    setAirspaceSublayers,
    timerange
}) => {
    const [operationVectorLayerVisible, setOperationVectorLayerVisible] = useState(true);
    const [constraintVectorLayerVisible, setConstraintVectorLayerVisible] = useState(true);
    const [alertVectorLayerVisible, setAlertVectorLayerVisible] = useState(true);
    const [coaVectorLayerVisible, setCoaVectorLayerVisible] = useState(true);

    const [airspaceLayersOpen, setAirspaceLayersOpen] = useState(false);
    const [airspaceVectorLayerVisible, setAirspaceVectorLayerVisible] = useState(true);

    const [facilityMapVectorLayerVisible, setFacilityMapVectorLayerVisible] = useState(true);
    const [stadiumVectorLayerVisible, setStadiumVectorLayerVisible] = useState(true);
    const [nationalSecurityNoFlyZoneVectorLayerVisible, setNationalSecurityNoFlyZoneVectorLayerVisible] = useState(true);

    const [specialUseAirspaceLayers, setSpecialUseAirspaceLayers] = useState([
        { title: "Prohibited Airspaces", type_code: "P", visible: true },
        { title: "Restricted Airspaces", type_code: "R", visible: true },
        { title: "Military Operation Areas", type_code: "MOA", visible: true },
        { title: "Warning Areas", type_code: "W", visible: true },
        { title: "Alert Areas", type_code: "A", visible: true }
    ]);
    const [tfrVectorLayerVisible, setTfrVectorLayerVisible] = useState(true);
    const [airportVectorLayerVisible, setAirportVectorLayerVisible] = useState(true);
    const [dcFrzSfraVectorLayerVisible, setDcFrzSfraVectorLayerVisible] = useState(true);

    const externalFlightsAreVisible = layers?.externalFlights;
    const [externalFlightsVisible, handleToggleExternalFlightsLayer] = useExternalFlightsLayer(map, externalFlightsAreVisible, timerange);

    const [disabled, setDisabled] = useState(false);

    const { laancTileServerUrl } = useEnv();

    const mapDropdownStyles = useMapDropdownStyles(mapLayersAnchorEl);

    // initialize layers
    useEffect(() => {
        if (!map) return;

        initializeMapLayers();
    }, [map]);

    const initializeMapLayers = async () => {
        const statuses = await getTileserverStatuses(laancTileServerUrl);
        if (!statuses || statuses.laanc_tileserver === false) {
            setDisabled(true);
            return;
        }
        const laancTilesets = [
            { tileset_id: "LAANC_AIRSPACE", min_zoom: 0, max_zoom: 14 },
            { tileset_id: "UASFM", min_zoom: 9, max_zoom: 14 },
            { tileset_id: "FT_NSUFR", min_zoom: 0, max_zoom: 14 },
            { tileset_id: "PT_NSUFR", min_zoom: 0, max_zoom: 14 },
            { tileset_id: "SU_AIRSPACE", min_zoom: 0, max_zoom: 13 },
            // { tileset_id: "STADIUMS", min_zoom: 0, max_zoom: 14 },
            { tileset_id: "SEAMS_STADIUMS", min_zoom: 0, max_zoom: 14 },
            { tileset_id: "FAA_TFR", min_zoom: 0, max_zoom: 14 },
            { tileset_id: "AIRPORTS", min_zoom: 9, max_zoom: 14 },
            { tileset_id: "DC_FRZ_SFRA", min_zoom: 0, max_zoom: 14 }
        ];
        laancTilesets.forEach(({ tileset_id: id, min_zoom: minZoom, max_zoom: maxZoom }) => {
            if (id === "LAANC_AIRSPACE" && !layers?.airspaces) {
                return;
            } else if (id !== "LAANC_AIRSPACE" && !layers?.laanc) {
                return;
            }

            let style = undefined;
            if (id === "LAANC_AIRSPACE") {
                style = (feature) => GetLaancAirspaceStyle(feature, airspaceSubLayers);
            } else {
                style = (feature) => GetLaancStyle(feature);
            }

            const source = new VectorTileSource({
                format: new MVT(),
                url: `${laancTileServerUrl}/data/${id}/{z}/{x}/{y}.pbf?time=${new Date().getTime()}`,
                maxZoom: maxZoom,
                minZoom: minZoom
            });
            const layer = new VectorTileLayer({
                source: source,
                style: style,
                minZoom: minZoom,
                properties: { name: id },
                zIndex: 1
            });
            map.addLayer(layer);
        });
    };

    const handleAirspaceLayerCheckboxClick = (localTypes, value) => {
        const sublayers = airspaceSubLayers.map((setting) => {
            if (localTypes.includes(setting.local_type)) {
                return {
                    ...setting,
                    visible: value
                };
            } else {
                return setting;
            }
        });
        setAirspaceSublayers(sublayers);

        if (sublayers.every(({ visible }) => visible === sublayers[0].visible)) {
            setAirspaceVectorLayerVisible(sublayers[0].visible);
        }
        const airspaces = GetLayer(map, "LAANC_AIRSPACE");
        airspaces.setStyle((feature) => {
            return GetLaancAirspaceStyle(feature, sublayers);
        });
    };
    const handleLaancLayerCheckboxClick = (layerName, typeCode, visibility) => {
        if (layerName.includes("flights")) {
            setOperationVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName.includes("constraints")) {
            setConstraintVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName.includes("coa")) {
            setCoaVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName.includes("alerts")) {
            setAlertVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName.includes("UASFM")) {
            setFacilityMapVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName.includes("STADIUMS")) {
            setStadiumVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName.includes("NSUFR")) {
            setNationalSecurityNoFlyZoneVectorLayerVisible(visibility);
            handleToggleNationalSecurityNoFlyZoneLayerVisibility(visibility);
        } else if (layerName.includes("SU_AIRSPACE")) {
            const suAirspaces = specialUseAirspaceLayers.map((layer) => {
                if (layer.type_code === typeCode) {
                    return { ...layer, visible: visibility };
                } else {
                    return layer;
                }
            });
            setSpecialUseAirspaceLayers(suAirspaces);
            handleToggleSpecialUseAirspaceLayerVisibility(suAirspaces);
        } else if (layerName.includes("FAA_TFR")) {
            setTfrVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName.includes("AIRPORTS")) {
            setAirportVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName.includes("DC_FRZ_SFRA")) {
            setDcFrzSfraVectorLayerVisible(visibility);
            handleToggleLayerVisibility(layerName, visibility);
        } else if (layerName === "externalFlights") {
            handleToggleExternalFlightsLayer();
        }
    };
    const handleToggleLayerVisibility = (layerName, visibility) => {
        const layer = GetLayer(map, layerName);
        if (layer) {
            layer.setVisible(visibility);
        }
    };
    const handleToggleNationalSecurityNoFlyZoneLayerVisibility = (visibility) => {
        const fullTimeNSUFR = GetLayer(map, "FT_NSUFR");
        if (fullTimeNSUFR) {
            fullTimeNSUFR.setVisible(visibility);
        }

        const partTimeNSUFR = GetLayer(map, "PT_NSUFR");
        if (partTimeNSUFR) {
            partTimeNSUFR.setVisible(visibility);
        }
    };
    const handleToggleSpecialUseAirspaceLayerVisibility = (updatedSuAirspaces) => {
        const suAirspaces = GetLayer(map, "SU_AIRSPACE");
        if (suAirspaces) {
            suAirspaces.setStyle((feature) => {
                return getSpecialUseAirspaceStyle(feature, updatedSuAirspaces);
            });
        }
    };
    const handleCloseLaancLayersDropdown = () => {
        setMapLayersOpen(false);
        setMapLayersAnchorEl(null);
    };
    return (
        <Popover
            open={mapLayersOpen}
            anchorEl={mapLayersAnchorEl}
            onClose={handleCloseLaancLayersDropdown}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { ...mapDropdownStyles } } }}
        >
            <List dense>
                {[
                    {
                        id: "flights",
                        title: "Operations",
                        checked: operationVectorLayerVisible,
                        visible: layers?.operations,
                        disabled: false
                    },
                    {
                        id: "constraints",
                        title: "Constraints",
                        checked: constraintVectorLayerVisible,
                        visible: layers?.constraints,
                        disabled: false
                    },
                    {
                        id: "alerts",
                        title: "Alert Volumes",
                        checked: alertVectorLayerVisible,
                        visible: layers?.alerts,
                        disabled: false
                    },
                    {
                        id: "coa",
                        title: "COA Volumes",
                        checked: coaVectorLayerVisible,
                        visible: layers?.coa,
                        disabled: false
                    },
                    {
                        id: "externalFlights",
                        title: "External Flights",
                        checked: disabled ? false : externalFlightsVisible,
                        visible: layers?.externalFlights,
                        disabled: false
                    },
                    {
                        id: "LAANC_AIRSPACE",
                        title: "Airspaces",
                        checked: disabled ? false : airspaceSubLayers.every(({ visible }) => visible),
                        visible: layers?.airspaces,
                        disabled: disabled
                    },
                    {
                        id: "UASFM",
                        title: "Facility Maps",
                        checked: disabled ? false : facilityMapVectorLayerVisible,
                        visible: layers?.laanc,
                        disabled: disabled
                    },
                    {
                        id: "SEAMS_STADIUMS",
                        title: "Stadiums",
                        checked: disabled ? false : stadiumVectorLayerVisible,
                        visible: layers?.laanc,
                        disabled: disabled
                    },
                    {
                        id: "NSUFR",
                        title: "National Security UAS Flight Restrictions",
                        checked: disabled ? false : nationalSecurityNoFlyZoneVectorLayerVisible,
                        visible: layers?.laanc,
                        disabled: disabled
                    },
                    ...specialUseAirspaceLayers.map(({ title, type_code, visible }) => {
                        return {
                            id: "SU_AIRSPACE",
                            title: title,
                            checked: disabled ? false : visible,
                            visible: layers?.laanc,
                            type_code: type_code,
                            disabled: disabled
                        };
                    }),
                    {
                        id: "FAA_TFR",
                        title: "Temporary Flight Restrictions",
                        checked: disabled ? false : tfrVectorLayerVisible,
                        visible: layers?.laanc,
                        disabled: disabled
                    },
                    {
                        id: "AIRPORTS",
                        title: "Airports",
                        checked: disabled ? false : airportVectorLayerVisible,
                        visible: layers?.laanc,
                        disabled: disabled
                    },
                    {
                        id: "DC_FRZ_SFRA",
                        title: "DC FRZ SFRA",
                        checked: disabled ? false : dcFrzSfraVectorLayerVisible,
                        visible: layers?.laanc,
                        disabled: disabled
                    }
                ].map((option, i) => {
                    if (!option.visible) {
                        return;
                    }
                    const border = getBorderFromMapLayerOption(option);
                    return option.title !== "Airspaces" ? (
                        <Tooltip key={i} title={disabled ? "This layer temporarily unavailable." : ""} placement="top">
                            <ListItem
                                dense
                                secondaryAction={
                                    <Checkbox
                                        checked={option.checked}
                                        onChange={() => handleLaancLayerCheckboxClick(option.id, option.type_code, !option.checked)}
                                        size="small"
                                        disabled={option.disabled}
                                    />
                                }
                                sx={{ padding: "8px 48px 8px 8px" }}
                            >
                                <ListItemIcon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {option.title !== "Airports" ? (
                                        <Box sx={{ height: "20px", width: "20px", border: border }} />
                                    ) : (
                                        <img src={`${AirplaneIcon}`} alt="Airplane Icon" />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary={option.title} slotProps={{ primary: { color: option.disabled ? "textDisabled" : "inherit" } }} />
                            </ListItem>
                        </Tooltip>
                    ) : (
                        <React.Fragment key={i}>
                            <Tooltip title={disabled ? "This layer temporarily unavailable." : ""} placement="top">
                                <ListItem
                                    dense
                                    secondaryAction={
                                        <Box sx={{ display: "flex", flexDirection: "row-reverse", alignItems: "center", justifyContent: "center" }}>
                                            <Checkbox
                                                size="small"
                                                checked={option.checked}
                                                indeterminate={!airspaceSubLayers.every(({ visible }) => visible === airspaceSubLayers[0].visible)}
                                                onChange={() =>
                                                    handleAirspaceLayerCheckboxClick(["CLASS_B", "CLASS_C", "CLASS_D", "CLASS_E2"], !airspaceVectorLayerVisible)
                                                }
                                                disabled={option.disabled}
                                            />
                                            {option.disabled === false ? (
                                                airspaceLayersOpen ? (
                                                    <ExpandLessIcon onClick={() => setAirspaceLayersOpen(false)} />
                                                ) : (
                                                    <ExpandMoreIcon onClick={() => setAirspaceLayersOpen(true)} />
                                                )
                                            ) : (
                                                <></>
                                            )}
                                        </Box>
                                    }
                                    sx={{ padding: "8px 48px 8px 8px" }}
                                >
                                    <ListItemIcon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <Box sx={{ height: "20px", width: "20px", border: border }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        id={option.id}
                                        primary={option.title}
                                        slotProps={{ primary: { color: option.disabled ? "textDisabled" : "inherit" } }}
                                    />
                                </ListItem>
                            </Tooltip>
                            <Collapse in={airspaceLayersOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {airspaceSubLayers.map((airspace, index) => (
                                        <ListItem
                                            key={index}
                                            dense
                                            sx={{ padding: "8px 48px 8px 8px" }}
                                            secondaryAction={
                                                <Checkbox
                                                    checked={airspace.visible}
                                                    onChange={() => handleAirspaceLayerCheckboxClick([airspace.local_type], !airspace.visible)}
                                                    size="small"
                                                />
                                            }
                                        >
                                            <ListItemIcon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                <Box sx={{ height: "20px", width: "20px", border: airspace.border }} />
                                            </ListItemIcon>
                                            <ListItemText primary={airspace.local_type} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    );
                })}
            </List>
        </Popover>
    );
};

export default MapLaancLayersDropdown;
